// Head to Head — the raw list of past meetings for a fixture (Phase 3 → M2 wave 6).
//
// A flat match list, most-recent first, optionally filtered to the fixture-home
// team's home/away meetings (ha-toggle). There is no animation: the legacy uses
// a d3 keyed row-join purely to rebuild the rows, with no transition. So React
// owns the `.data-wrapper` and a useEffect builds the rows imperatively — this is
// needed because each team name is fit to the column width via the legacy
// `window.fitTeamName` (a live text measurement React can't do declaratively
// without a measure pass). Emits the SAME DOM + classes as the legacy
// renderH2HModule (verified by the VRT baseline). Data now comes from app-api via
// `useWidgetData` (was the `getH2HData` legacyData getter); the shaper is
// unchanged. Venue reshapes the fetched payload client-side, no refetch.

import { useEffect, useRef } from "react";
import { clubFromName, shapeH2H, type H2HRow } from "@bento/shared";
import { useWidgetData } from "../../data/useWidgetData.js";
import { useFixtureKey } from "../../data/useFixtureKey.js";
import WidgetSkeleton from "./WidgetSkeleton.js";
import WidgetError from "./WidgetError.js";

// The legacy name-fitter lives on window (js/clubs.js). Reuse it so the fitted
// text matches legacy exactly; it returns the raw name when unavailable.
type FitTeamName = (name: string, availPx: number, font: string) => string;
function fitTeamName(name: string, availPx: number, font: string): string {
  const fn = (window as unknown as { fitTeamName?: FitTeamName }).fitTeamName;
  return fn ? fn(name, availPx, font) : name;
}

export interface HeadToHeadProps {
  fixture: string;
  /** Venue toggle: "All" | "Home" | "Away"; defaults to "All". */
  ha?: string | null;
}

type Tier = "name" | "medium" | "short";
let measureCtx: CanvasRenderingContext2D | null = null;
function phoneTier(rows: H2HRow[], availPx: number, font: string): Tier {
  measureCtx ??= document.createElement("canvas").getContext("2d");
  if (!measureCtx) return "short";
  measureCtx.font = font;
  const names = rows.flatMap((m) => [m.home, m.away]);
  for (const tier of ["name", "medium"] as const) {
    if (names.every((n) => measureCtx!.measureText(clubFromName(n)?.[tier] ?? n).width <= availPx))
      return tier;
  }
  return "short";
}

function buildRows(wrapper: HTMLElement, rows: H2HRow[]): void {
  // H2H flex layout: col-team (flex:4) + h2h-score (flex:2) + col-team (flex:4)
  // = 10 units, so each team column is 40% of the wrapper. Subtract padding-right
  // (10px) for the text area. Min 40px (matches the legacy) so it never goes < 0.
  const teamColW = Math.max(40, Math.floor(wrapper.offsetWidth * 0.4) - 10);
  const font = window.getComputedStyle(wrapper).font;
  // Phones: fixed 20% team columns (styles.css) — use the longest name form
  // (Arsenal → Arsenal / Arsenal / ARS) that fits EVERY team in the list, so
  // names stay consistent; elsewhere, the full name shortened only to fit.
  const phone = window.innerWidth <= 768;
  const tier = phone ? phoneTier(rows, wrapper.offsetWidth * 0.2 - 6, font) : null;
  const fit = (name: string): string =>
    tier ? (clubFromName(name)?.[tier] ?? name) : fitTeamName(name, teamColW, font);
  // Phones: a short UK date (2025-09-01 → 01/09/25) so all four columns fit.
  const day = (d: string): string => {
    const iso = phone && /^(\d{4})-(\d{2})-(\d{2})$/.exec(d);
    return iso ? `${iso[3]}/${iso[2]}/${iso[1]!.slice(2)}` : d;
  };

  wrapper.innerHTML = rows
    .map(
      (m) =>
        `<div class="data-row">` +
        `<span class="col-team${m.homeWin ? " h2h-winner" : ""}">${fit(m.home)}</span>` +
        `<span class="h2h-score">${m.score}</span>` +
        `<span class="col-team${m.awayWin ? " h2h-winner" : ""}">${fit(m.away)}</span>` +
        `<span class="col-opt h2h-date">${day(m.date)}</span>` +
        `</div>`
    )
    .join("");
}

export default function HeadToHead({ fixture, ha }: HeadToHeadProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const venue = ha || "All";
  const { data, isPending, isError, refetch } = useWidgetData(
    "head-to-head",
    useFixtureKey(fixture)
  );
  const rows = shapeH2H(data ?? {}, fixture || "Arsenal v Everton", venue);
  // `sig` captures fixture + venue + row count — the complete draw input. Row
  // count folds in the fetch state: while pending it's 0, so the effect re-runs
  // once the data resolves and the rows appear (the wrapper mounts only after the
  // early returns below). `rows` is derived fresh each render.
  const sig = `${fixture}|${venue}|${rows.length}`;

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    buildRows(wrapper, rows);
    if (typeof ResizeObserver === "undefined") return; // (jsdom tests)
    // Re-fit the names when the module's width changes (phone pairing/resize).
    let w = wrapper.offsetWidth;
    const ro = new ResizeObserver(() => {
      if (wrapper.offsetWidth === w) return;
      w = wrapper.offsetWidth;
      buildRows(wrapper, rows);
    });
    ro.observe(wrapper);
    return () => ro.disconnect();
  }, [sig]);

  if (isPending) return <WidgetSkeleton />;
  if (isError) return <WidgetError onRetry={refetch} />;

  return <div className="data-wrapper top-align" ref={wrapperRef} />;
}
